import { useAuth } from "@clerk/clerk-react";
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  AddOnDetails,
  AddOnItemName,
  BannerPromoCode,
  CustomDomainInfo,
  SearchParams,
  Subscription,
  SubscriptionPlanTier,
  UpgradeDialogSource,
} from "schemas/dashboard";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";

import { OrganizationUserContext } from "./Organization";

export enum SubscriptionTab {
  yearly = "yearly",
  monthly = "monthly",
  quarterly = "quarterly",
}

export const PRO_EMAIL_MONTHLY_LIMIT = 250;

// Ceiling for total email credits; rollover doesn't apply at/above it. VA can
// build higher (and accrue faster) than other plans.
export const EMAIL_CREDIT_ROLLOVER_CAP_DEFAULT = 1000;
export const EMAIL_CREDIT_ROLLOVER_CAP_VA = 1500;
export const EMAIL_CREDIT_ROLLOVER_MONTHLY_DEFAULT = 250;
export const EMAIL_CREDIT_ROLLOVER_MONTHLY_VA = 500;

export enum SplashPage {
  annual = "annual",
  instagram = "instagram",
  ooo = "ooo",
  pro_bundle = "pro_bundle",
  va_money_back = "va_money_back",
  repitching = "repitching",
  pro = "pro",
  va = "va",
  va_bundled = "va_bundled",
  strategy_package = "strategy_package",
  b2go = "b2go",
}

export interface CheckoutState {
  plan?: Plans | AddOnItemName;
  pageSource?: string | null;
  source?: string | null;
  coupon?: string | null;
  domainInfo?: CustomDomainInfo;
  splashPageSource?: SplashPage | null;
  fromSubscribePage?: boolean;
}

export type AllPlans = {
  [key in Plans]: {
    display_name: string;
    monthly_limit: number;
    price: number;
    chat_monthly_limit: number;
    description: string;
    product: string;
    terms_and_conditions?: string;
    price_annotation?: string;
    tier: SubscriptionPlanTier;
  };
};

interface SubscriptionContextInterface {
  subscription: Subscription | null;
  setSubscription: React.Dispatch<SetStateAction<Subscription | null>>;
  clearSubscriptionContext: () => void;
  plans: AllPlans | null;
  addOns: { [key: string]: AddOnDetails };
  fetchPlans: () => void;
  isProPlan: boolean;
  isFreePlan: boolean;
  isStarterPlan: boolean;
  isVaPlan: boolean;
  isAutopilotPlan: boolean;
  isAtEmailCreditCap: boolean;
  referralCode: string | null;
  setReferralCode: React.Dispatch<SetStateAction<string | null>>;
  setUpgradeDialogSource: React.Dispatch<
    SetStateAction<UpgradeDialogSource | null>
  >;
  upgradeDialogSource: UpgradeDialogSource | null;
  emailSents: number;
  customizedMessagesDrafted: number;
  tab: SubscriptionTab;
  setTab: Dispatch<SetStateAction<SubscriptionTab>>;
  bannerDismissed: boolean;
  dismissBanner: () => void;
  openCheckoutDialog: CheckoutState | null;
  setOpenCheckoutDialog: React.Dispatch<SetStateAction<CheckoutState | null>>;
  referralDialog: boolean;
  setReferralDialog: React.Dispatch<SetStateAction<boolean>>;
  customDomainDialog: boolean;
  setCustomDomainDialog: React.Dispatch<SetStateAction<boolean>>;
  cancelDialog: boolean;
  setCancelDialog: React.Dispatch<SetStateAction<boolean>>;
  manageDialog: boolean;
  setManageDialog: React.Dispatch<SetStateAction<boolean>>;
  plan: Plans | AddOnItemName | null;
  setPlan: React.Dispatch<SetStateAction<Plans | AddOnItemName | null>>;
  hasPastDue: boolean;
  setHasPastDue: React.Dispatch<SetStateAction<boolean>>;
  bannerPromoCodes: BannerPromoCode[];
  remainingTemplateEvaluations: number | null;
  setRemainingTemplateEvaluations: React.Dispatch<
    SetStateAction<number | null>
  >;
  totalTemplateEvaluations: number | null;
  setTotalTemplateEvaluations: React.Dispatch<SetStateAction<number | null>>;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface = {
  subscription: null,
  setSubscription: defaultContextMissingFunction,
  updateSubscription: defaultContextMissingFunction,
  clearSubscriptionContext: defaultContextMissingFunction,
  plans: null,
  addOns: {},
  fetchPlans: defaultContextMissingFunction,
  isProPlan: false,
  isFreePlan: true,
  isStarterPlan: false,
  isVaPlan: false,
  isAutopilotPlan: false,
  isAtEmailCreditCap: false,
  referralCode: null,
  setReferralCode: defaultContextMissingFunction,
  upgradeDialogSource: null,
  setUpgradeDialogSource: defaultContextMissingFunction,
  emailSents: 0,
  customizedMessagesDrafted: 0,
  tab: SubscriptionTab.yearly,
  setTab: defaultContextMissingFunction,
  bannerDismissed: false,
  dismissBanner: defaultContextMissingFunction,
  openCheckoutDialog: null,
  setOpenCheckoutDialog: defaultContextMissingFunction,
  referralDialog: false,
  setReferralDialog: defaultContextMissingFunction,
  customDomainDialog: false,
  setCustomDomainDialog: defaultContextMissingFunction,
  cancelDialog: false,
  setCancelDialog: defaultContextMissingFunction,
  manageDialog: false,
  setManageDialog: defaultContextMissingFunction,
  plan: null,
  setPlan: defaultContextMissingFunction,
  hasPastDue: false,
  setHasPastDue: defaultContextMissingFunction,
  bannerPromoCodes: [],
  remainingTemplateEvaluations: null,
  setRemainingTemplateEvaluations: defaultContextMissingFunction,
  totalTemplateEvaluations: null,
  setTotalTemplateEvaluations: defaultContextMissingFunction,
};

const SubscriptionContext =
  createContext<SubscriptionContextInterface>(defaultInterface);

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

const SubscriptionProvider = ({ children }: SubscriptionProviderProps) => {
  const { getToken } = useAuth();
  const [searchParams] = useSearchParams();
  const { currentOrg } = useContext(OrganizationUserContext);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plan, setPlan] = useState<Plans | AddOnItemName | null>(null);
  const [upgradeDialogSource, setUpgradeDialogSource] =
    useState<UpgradeDialogSource | null>(null);
  const [plans, setPlans] = useState<AllPlans | null>(null);
  const [addOns, setAddOns] = useState<{
    [key: string]: AddOnDetails;
  }>({});
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [tab, setTab] = useState<SubscriptionTab>(SubscriptionTab.yearly);
  const [bannerDismissed, setBannerDismissed] = useState<boolean>(false);
  const dismissBanner = () => setBannerDismissed(true);
  const [openCheckoutDialog, setOpenCheckoutDialog] =
    useState<CheckoutState | null>(null);
  const [referralDialog, setReferralDialog] = useState<boolean>(false);
  const [customDomainDialog, setCustomDomainDialog] = useState<boolean>(false);
  const [cancelDialog, setCancelDialog] = useState<boolean>(false);
  const [manageDialog, setManageDialog] = useState<boolean>(false);
  const [hasPastDue, setHasPastDue] = useState<boolean>(false);
  const [bannerPromoCodes, setBannerPromoCodes] = useState<BannerPromoCode[]>(
    [],
  );
  const [remainingTemplateEvaluations, setRemainingTemplateEvaluations] =
    useState<number | null>(null);
  const [totalTemplateEvaluations, setTotalTemplateEvaluations] = useState<
    number | null
  >(null);

  const clearSubscriptionContext = () => {
    setSubscription(null);
  };

  const fetchPlans = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/subscription-plans`,
      );
      setPlans(res);
    } catch (error) {}
  };

  const fetchAddOns = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons`,
      );
      setAddOns(res);
    } catch (error) {}
  };

  const fetchReferralCode = async () => {
    try {
      const { referralCode } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/referrals/code`,
      );
      setReferralCode(referralCode);
    } catch (error) {}
  };

  const fetchCoupons = async () => {
    try {
      const isMobileApp =
        searchParams.get(SearchParams.PAGE_SOURCE) === "mobile_app";
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/coupons${
          isMobileApp ? "?app_source=mobile" : ""
        }`,
      );
      setBannerPromoCodes(res.bannerPromoCodes);
    } catch (error) {}
  };

  const isStarterPlan = useMemo(() => {
    return (
      subscription?.subscription?.planName === Plans.STARTER ||
      subscription?.subscription?.planName === Plans.STARTER_YEARLY
    );
  }, [subscription]);

  const isProPlan = useMemo(() => {
    return (
      subscription?.subscription?.planName === Plans.PRO ||
      subscription?.subscription?.planName === Plans.PRO_YEARLY ||
      subscription?.subscription?.planName === Plans.PRO_QUARTERLY ||
      isStarterPlan
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription]);

  const isFreePlan = useMemo(() => {
    return subscription?.subscription?.planName === Plans.FREE;
  }, [subscription]);

  const isVaPlan = useMemo(() => {
    return (
      subscription?.subscription?.planName === Plans.VA ||
      subscription?.subscription?.planName === Plans.VA_YEARLY ||
      subscription?.subscription?.planName === Plans.VA_QUARTERLY
    );
  }, [subscription]);

  const isAutopilotPlan = useMemo(() => {
    return (
      subscription?.subscription?.planName === Plans.AUTOPILOT ||
      subscription?.subscription?.planName === Plans.AUTOPILOT_YEARLY ||
      subscription?.subscription?.planName === Plans.AUTOPILOT_QUARTERLY
    );
  }, [subscription]);

  // Base allotment (plan + add-ons, excluding rollover) is at the cap. Covers
  // grandfathered high-volume plans and add-on-stacked users.
  const isAtEmailCreditCap = useMemo(() => {
    const baseAllotment =
      (subscription?.monthlyLimit || 0) -
      (subscription?.rolloverEmailCredits || 0);
    const cap = isVaPlan
      ? EMAIL_CREDIT_ROLLOVER_CAP_VA
      : EMAIL_CREDIT_ROLLOVER_CAP_DEFAULT;
    return baseAllotment >= cap;
  }, [subscription, isVaPlan]);

  const emailSents = subscription?.monthlyLimit
    ? subscription?.monthlyLimit - subscription?.remainingMessages
    : 0;
  const customizedMessagesDrafted = subscription?.chatMonthlyLimit
    ? subscription.chatMonthlyLimit -
      (subscription.remainingMonthlyChatMessages ??
        subscription.remainingChatMessages)
    : 0;

  useEffect(() => {
    if (currentOrg?.id) {
      fetchPlans();
      fetchReferralCode();
      fetchAddOns();
      fetchCoupons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const updateTab =
    subscription?.subscription?.planName && isPage(routes.settings);
  useEffect(() => {
    if (updateTab) {
      const isMonthly =
        subscription?.subscription?.planName === Plans.PRO ||
        subscription?.subscription?.planName === Plans.STARTER ||
        subscription?.subscription?.planName === Plans.VA ||
        subscription?.subscription?.planName === Plans.AUTOPILOT;
      if (
        isMonthly &&
        tab === SubscriptionTab.yearly &&
        isPage(routes.settings)
      ) {
        setTab(SubscriptionTab.monthly);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTab]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        setSubscription,
        upgradeDialogSource,
        setUpgradeDialogSource,
        clearSubscriptionContext,
        plans,
        addOns,
        fetchPlans,
        isProPlan,
        isFreePlan,
        isStarterPlan,
        isVaPlan,
        isAutopilotPlan,
        isAtEmailCreditCap,
        referralCode,
        setReferralCode,
        emailSents,
        customizedMessagesDrafted,
        tab,
        setTab,
        bannerDismissed,
        dismissBanner,
        openCheckoutDialog,
        setOpenCheckoutDialog,
        referralDialog,
        setReferralDialog,
        customDomainDialog,
        setCustomDomainDialog,
        cancelDialog,
        setCancelDialog,
        manageDialog,
        setManageDialog,
        plan,
        setPlan,
        hasPastDue,
        setHasPastDue,
        bannerPromoCodes,
        remainingTemplateEvaluations,
        setRemainingTemplateEvaluations,
        totalTemplateEvaluations,
        setTotalTemplateEvaluations,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export { SubscriptionProvider, SubscriptionContext };
